import { Controller } from "@hotwired/stimulus"

// Click-and-drag horizontal scrolling for the arena tab bars (the overflow-x
// mode/skill pill rows). Mouse + pen only — touch and trackpads already pan
// natively, so hijacking them would fight the OS. A drag never activates the tab
// it ends on: the click that terminates a real drag is swallowed in the capture
// phase. No-ops when the row isn't actually overflowing, so tabs stay clickable
// on wide screens where everything fits.
export default class extends Controller {
  private dragging = false
  private moved = false
  private startX = 0
  private startLeft = 0

  connect() {
    this.element.addEventListener("pointerdown", this.onPointerDown)
    // Capture phase so a drag's terminal click is cancelled before it reaches a tab.
    this.element.addEventListener("click", this.onClick, true)
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this.onPointerDown)
    this.element.removeEventListener("click", this.onClick, true)
    this.endDrag()
  }

  private get scroller(): HTMLElement {
    return this.element as HTMLElement
  }

  private onPointerDown = (event: PointerEvent) => {
    if (event.pointerType === "touch" || event.button !== 0) return
    if (this.scroller.scrollWidth <= this.scroller.clientWidth) return
    this.dragging = true
    this.moved = false
    this.startX = event.clientX
    this.startLeft = this.scroller.scrollLeft
    window.addEventListener("pointermove", this.onPointerMove)
    window.addEventListener("pointerup", this.onPointerUp)
    this.scroller.style.cursor = "grabbing"
    this.scroller.style.userSelect = "none"
  }

  private onPointerMove = (event: PointerEvent) => {
    if (!this.dragging) return
    const dx = event.clientX - this.startX
    if (Math.abs(dx) > 4) this.moved = true
    this.scroller.scrollLeft = this.startLeft - dx
  }

  private onPointerUp = () => this.endDrag()

  private endDrag() {
    if (!this.dragging) return
    this.dragging = false
    window.removeEventListener("pointermove", this.onPointerMove)
    window.removeEventListener("pointerup", this.onPointerUp)
    this.scroller.style.cursor = ""
    this.scroller.style.userSelect = ""
  }

  private onClick = (event: MouseEvent) => {
    if (!this.moved) return
    event.preventDefault()
    event.stopPropagation()
    this.moved = false
  }
}
